/**
 * DevBasicsEdit — ① 何を開発しますか の項目を登録後に直す。
 *
 * ピッカーは新しく作らない。想定利用者・担当課 と 設置予定場所 は
 * 設備を登録（製造管理）と同じ SearchableMaster を、候補は同じマスタ
 * （社員 / 部門 / 場所マスタ）から取る。
 *
 * 開発の種類 はここでは変えられない（後の登録先が変わる項目のため、
 * 変更ルールの判断待ち）。読み取り専用で理由を添えて出す。
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchableMaster from '../../mfg/SearchableMaster';
import { EmployeePicker } from '@/components/registration/EmployeePicker';
import { engApi } from '../engApi';
import { devApi } from './devApi';
import { buildPersonSectionOptions, buildLocationOptions } from './pickerOptions';

export const DevBasicsEdit = ({ dev, onCancel, onSaved }) => {
  const isMfg = dev.development_type === 'mfg_equipment';
  const [projects, setProjects] = useState([]);
  const [depts, setDepts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    project_id: dev.project_id || '',
    target_department_ids: dev.target_department_ids || [],
    intended_users: dev.intended_users || '',
    install_location: dev.install_location || '',
    lead_name: dev.lead_name || '',
    member_names: dev.member_names || [],
    acceptance_name: dev.acceptance_name || '',
  });
  // 2026-10-23 · 担当・メンバー・受入担当は社員マスターから選ぶ（名前 → 社員ID）
  const active = employees.filter((e) => (e.status || 'active') === 'active');
  const idOf = (name) => active.find((e) => e.name === name)?.id || null;
  const inDept = f.target_department_ids.length
    ? active.filter((e) => f.target_department_ids.includes(e.department_id))
    : active;

  useEffect(() => {
    engApi.projects().then((p) => setProjects(p || [])).catch(() => {});
    devApi.departments().then((d) => setDepts(d || [])).catch(() => {});
    devApi.employees().then((e) => setEmployees(e || [])).catch(() => {});
    devApi.mfgLocations().then((l) => setLocations(l || [])).catch(() => {});
  }, []);

  const save = async () => {
    if (isMfg && !f.target_department_ids.length) return toast.error('導入先の部門・エリアを選んでください');
    if (isMfg && !f.intended_users.trim()) return toast.error('想定利用者・担当課を選んでください');
    setBusy(true);
    try {
      await devApi.patch(dev.id, {
        project_id: f.project_id || undefined,
        target_department_ids: f.target_department_ids,
        intended_users: f.intended_users,
        install_location: f.install_location,
        lead_employee_id: idOf(f.lead_name) || '',
        member_employee_ids: f.member_names.map(idOf).filter(Boolean),
        acceptance_employee_id: idOf(f.acceptance_name) || '',
      });
      toast.success('案件の基本情報を更新しました');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '更新できませんでした');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 border-t border-zinc-200 p-4" data-testid="dev-basics-edit">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-semibold text-zinc-700">担当（1人）</Label>
          <EmployeePicker value={f.lead_name} employees={active}
            onChange={(v) => setF({ ...f, lead_name: v })} testId="dev-basics-lead" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-700">メンバー（複数）</Label>
          <EmployeePicker value="" employees={active.filter((e) => e.name !== f.lead_name
            && !f.member_names.includes(e.name))}
            onChange={(v) => v && setF({ ...f, member_names: [...f.member_names, v] })}
            testId="dev-basics-member-add" />
          <div className="mt-1 flex flex-wrap gap-1" data-testid="dev-basics-members">
            {f.member_names.map((n) => (
              <button key={n} type="button"
                onClick={() => setF({ ...f, member_names: f.member_names.filter((x) => x !== n) })}
                className="rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-700 hover:border-rose-300 hover:text-rose-700"
                data-testid={`dev-basics-member-${n}`}>
                {n} ×
              </button>
            ))}
            {f.member_names.length === 0 && <span className="text-xs text-zinc-400">なし</span>}
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-700">受入担当（製造側）</Label>
          <EmployeePicker value={f.acceptance_name} employees={inDept}
            onChange={(v) => setF({ ...f, acceptance_name: v })} testId="dev-basics-acceptance" />
          <p className="mt-1 text-[11px] text-zinc-500">
            引き渡したあと、設備の担当者になります（対象部署の社員から選びます）。
          </p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-700">開発の種類</Label>
          <p className="mt-1 text-sm text-zinc-900" data-testid="dev-basics-type-readonly">
            {dev.development_type_label}
          </p>
          <p className="mt-1 text-xs text-zinc-600">
            種類は後の登録先（設備・製品）に関わるため、ここでは変更できません。
          </p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-700">プロジェクト</Label>
          <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v })}>
            <SelectTrigger className="mt-1" data-testid="dev-basics-project-select">
              <SelectValue placeholder="選択してください" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isMfg && (
          <>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">導入先の部門・エリア（必須）</Label>
              <Select value={f.target_department_ids[0] || ''}
                onValueChange={(v) => setF({ ...f, target_department_ids: [v] })}>
                <SelectTrigger className="mt-1" data-testid="dev-basics-dept-select">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">想定利用者・担当課（必須）</Label>
              <SearchableMaster testid="dev-basics-users" value={f.intended_users}
                options={buildPersonSectionOptions(employees, depts)} canWrite={false}
                valueField="name" allowNone={false}
                onChange={(v) => setF({ ...f, intended_users: v })}
                labels={{ searchPh: 'mfg.owner.searchPh', noMatch: 'mfg.owner.noMatch' }} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">設置予定場所（分かる場合）</Label>
              <SearchableMaster testid="dev-basics-location" value={f.install_location}
                options={buildLocationOptions(locations)} canWrite={false} valueField="name"
                onChange={(v) => setF({ ...f, install_location: v })}
                labels={{ searchPh: 'mfg.owner.searchPh', noMatch: 'mfg.owner.noMatch' }} />
            </div>
          </>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="dev-basics-cancel-btn">
          キャンセル
        </Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid="dev-basics-save-btn">
          {busy ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  );
};
